import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Clickble from "./Clickble";
import Button from "./Button";
import OneTimePassword from "../models/OneTimePassword";
import ResultStatus from "../classes/ResultStatus";
import ScrollView2 from "./ScrollView2";
import LoginResult from "../classes/LoginResult";
import Developer from "../models/Developer";
import TextView from "./TextView";
import Result from "../classes/Result";
import Query from "../classes/Query";
import LabelWithStringField from "./LabelWithStringField";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

type _BuilderEmailVerificationViewOnLoginClick = () => void;

type _BuilderEmailVerificationViewOnVerificationSuccess = (
  user: Developer
) => void;

type _ResendBtnOnPressed = (d3eState: BuilderEmailVerificationViewRefs) => void;

type _VerifyBtnOnPressed = (d3eState: BuilderEmailVerificationViewRefs) => void;

type _OtpboxOnChanged = (
  text: string,
  d3eState: BuilderEmailVerificationViewRefs
) => void;

export interface BuilderEmailVerificationViewProps extends BaseUIProps {
  key?: string;
  email: string;
  generatedToken: string;
  onLoginClick: _BuilderEmailVerificationViewOnLoginClick;
  onVerificationSuccess: _BuilderEmailVerificationViewOnVerificationSuccess;
}
/// To store state data for BuilderEmailVerificationView
class BuilderEmailVerificationViewRefs {
  id1ScrollController: ui.ScrollController = new ui.ScrollController();
  public resendBtn: ResendBtnState = new ResendBtnState();
  public verifyBtn: VerifyBtnState = new VerifyBtnState();
}

interface VerifyBtnWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderEmailVerificationViewRefs;
  _onVerifySuccessHandler?: _VerifyBtnOnPressed;
}

class VerifyBtnState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _VerifyBtnWithState extends ObservableComponent<VerifyBtnWithStateProps> {
  verifyBtnFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: VerifyBtnWithStateProps) {
    super(props);

    this.initState();
  }
  public get verifyBtn(): VerifyBtnState {
    return this.props.d3eState.verifyBtn;
  }
  public get d3eState(): BuilderEmailVerificationViewRefs {
    return this.props.d3eState;
  }
  public get _onVerifySuccessHandler(): _VerifyBtnOnPressed {
    return this.props._onVerifySuccessHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("verifyBtn", null, this.verifyBtn);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["verifyBtn", "verifyBtn.", "verifyBtn.focus", "verifyBtn.hover"],
      this.rebuild
    );
  }
  public verifyBtnOnEnter(event): void {
    return this.verifyBtn.setHover(true);
  }
  public verifyBtnOnExit(event): void {
    return this.verifyBtn.setHover(false);
  }
  public verifyBtnOnFocusChange(val): void {
    return this.verifyBtn.setFocus(val);
  }
  public dispose(): void {
    this.verifyBtn.setHover(false);

    this.verifyBtn.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      height: 35,
      margin: ui.EdgeInsets.symmetric({
        horizontal: 10.0,
        vertical: 0.0,
        transitions: new Map(),
      }),
      width: 135,
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.verifyBtn.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.verifyBtn.focus
          ? cStyle.tButtonPrimaryButtonDecorationOnFocus
          : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.verifyBtn.disable,
        onPressed: () => {
          this._onVerifySuccessHandler(this.d3eState);
        },
        onFocusChange: (val) => {
          this.verifyBtnOnFocusChange(val);
        },
        child: TextView({ data: "Verify Code" }),
        onEnter: (event) => {
          this.verifyBtnOnEnter(event);
        },
        onExit: (event) => {
          this.verifyBtnOnExit(event);
        },
      }),
      className: "x87 hc vc",
    });
  }
}
function VerifyBtnWithState(props: VerifyBtnWithStateProps) {
  return React.createElement(_VerifyBtnWithState, props);
}

interface ResendBtnWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderEmailVerificationViewRefs;
  _onResendHandler?: _ResendBtnOnPressed;
}

class ResendBtnState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _ResendBtnWithState extends ObservableComponent<ResendBtnWithStateProps> {
  resendBtnFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ResendBtnWithStateProps) {
    super(props);

    this.initState();
  }
  public get resendBtn(): ResendBtnState {
    return this.props.d3eState.resendBtn;
  }
  public get d3eState(): BuilderEmailVerificationViewRefs {
    return this.props.d3eState;
  }
  public get _onResendHandler(): _ResendBtnOnPressed {
    return this.props._onResendHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("resendBtn", null, this.resendBtn);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["resendBtn", "resendBtn.", "resendBtn.focus", "resendBtn.hover"],
      this.rebuild
    );
  }
  public resendBtnOnEnter(event): void {
    return this.resendBtn.setHover(true);
  }
  public resendBtnOnExit(event): void {
    return this.resendBtn.setHover(false);
  }
  public resendBtnOnFocusChange(val): void {
    return this.resendBtn.setFocus(val);
  }
  public dispose(): void {
    this.resendBtn.setHover(false);

    this.resendBtn.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      height: 35,
      width: 135,
      child: Button({
        padding: cStyle.tButtonSecondaryButtonPaddingOn,
        decoration: this.resendBtn.hover
          ? cStyle.tButtonSecondaryButtonDecorationOnHover
          : this.resendBtn.focus
          ? cStyle.tButtonSecondaryButtonDecorationOnFocus
          : cStyle.tButtonSecondaryButtonDecorationOn,
        disable: this.resendBtn.disable,
        onPressed: () => {
          this._onResendHandler(this.d3eState);
        },
        onFocusChange: (val) => {
          this.resendBtnOnFocusChange(val);
        },
        child: TextView({ data: "Resend Code" }),
        onEnter: (event) => {
          this.resendBtnOnEnter(event);
        },
        onExit: (event) => {
          this.resendBtnOnExit(event);
        },
      }),
      className: "x682 hc vc",
    });
  }
}
function ResendBtnWithState(props: ResendBtnWithStateProps) {
  return React.createElement(_ResendBtnWithState, props);
}

class _BuilderEmailVerificationViewState extends ObservableComponent<BuilderEmailVerificationViewProps> {
  static defaultProps = {
    email: "",
    generatedToken: "",
    onLoginClick: null,
    onVerificationSuccess: null,
  };
  d3eState: BuilderEmailVerificationViewRefs =
    new BuilderEmailVerificationViewRefs();
  userEnteredOtp: string = "";
  showOtpValidationError: boolean = false;
  token: string = "";
  user: Developer = null;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: BuilderEmailVerificationViewProps) {
    super(props);

    this.initState();
  }
  public get email(): string {
    return this.props.email;
  }
  public get generatedToken(): string {
    return this.props.generatedToken;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.on(
      ["email", "showOtpValidationError", "userEnteredOtp"],
      this.rebuild
    );
  }
  public componentDidUpdate(
    prevProps: BuilderEmailVerificationViewProps
  ): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.email !== this.props.email) {
      this.fire("email", this);
    }

    if (prevProps.generatedToken !== this.props.generatedToken) {
      this.fire("generatedToken", this);
    }
  }
  public setUserEnteredOtp(val: string): void {
    let isValChanged: boolean = this.userEnteredOtp !== val;

    if (!isValChanged) {
      return;
    }

    this.userEnteredOtp = val;

    this.fire("userEnteredOtp", this);
  }
  public setShowOtpValidationError(val: boolean): void {
    let isValChanged: boolean = this.showOtpValidationError !== val;

    if (!isValChanged) {
      return;
    }

    this.showOtpValidationError = val;

    this.fire("showOtpValidationError", this);
  }
  public setToken(val: string): void {
    let isValChanged: boolean = this.token !== val;

    if (!isValChanged) {
      return;
    }

    this.token = val;

    this.fire("token", this);
  }
  public setUser(val: Developer): void {
    let isValChanged: boolean = this.user !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("user", this.user, val);

    this.user = val;

    this.fire("user", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ScrollView2({
      child: ui.Column({
        mainAxisAlignment: ui.MainAxisAlignment.center,
        children: [
          ui.Row({
            crossAxisAlignment: ui.CrossAxisAlignment.start,
            children: [
              Clickble({
                onTap: () => {
                  this.onTapHandler(this.d3eState);
                },
                child: ui.AssetImage({
                  path: "images/d3eOnboarding.png",
                  width: 50,
                  height: 50,
                  className: "xd2 hc vc",
                }),
                key: "0",
              }),
            ],
            className: "x9a hc h",
            key: "0",
          }),
          TextView({
            data: "Please Verify Your Email",
            style: new ui.TextStyle({
              color: cStyle.tTextViewBoldHeadingColorOn,
              fontSize: cStyle.tTextViewBoldHeadingFontSizeOn,
              fontWeight: cStyle.tTextViewBoldHeadingFontWeightOn,
            }),
            className: "x585 hc",
            key: "1",
          }),
          ui.Wrap({
            alignment: ui.WrapAlignment.center,
            crossAxisAlignment: ui.WrapCrossAlignment.center,
            children: [
              TextView({
                data:
                  "We have sent an email to " +
                  this.email +
                  " with a One-time code",
                textAlign: cStyle.tTextViewParagraphCenterTextAlignOn,
                style: new ui.TextStyle({
                  color: cStyle.tTextViewParagraphCenterColorOn,
                  fontSize: cStyle.tTextViewParagraphCenterFontSizeOn,
                }),
                className: "xb20 hc",
                key: "0",
              }),
              TextView({
                data: "Please enter the code below.",
                textAlign: cStyle.tTextViewParagraphCenterTextAlignOn,
                style: new ui.TextStyle({
                  color: cStyle.tTextViewParagraphCenterColorOn,
                  fontSize: cStyle.tTextViewParagraphCenterFontSizeOn,
                }),
                className: "x54 hc",
                key: "1",
              }),
            ],
            className: "x6f7 hc",
            key: "2",
          }),
          ui.Container({
            child: ui.Column({
              mainAxisAlignment: ui.MainAxisAlignment.center,
              crossAxisAlignment: ui.CrossAxisAlignment.center,
              children: [
                ui.Container({
                  width: 280,
                  child: LabelWithStringField({
                    name: "Enter One Time Code",
                    value: this.userEnteredOtp,
                    help: "Enter the code sent to your email",
                    helpUrl: "",
                    placeHolder: "Enter The Code",
                    autoFocus: true,
                    errors: this.showOtpValidationError
                      ? ["Please enter valid otp !"]
                      : [],
                    onTap: () => {
                      this.setOtpError(this.d3eState);
                    },
                    onChanged: (text) => {
                      this.otpboxonChanged(text, this.d3eState);

                      this.setOtpHandler(text, this.d3eState);
                    },
                  }),
                  key: "0",
                  className: "x5d hc",
                }),
              ],
              className: "x51 hc vc v",
            }),
            className: "x7c hc vc",
            key: "3",
          }),
          ui.Row({
            mainAxisAlignment: ui.MainAxisAlignment.center,
            children: [
              ResendBtnWithState({
                d3eState: this.d3eState,
                _onResendHandler: this.onResendHandler,
                key: "0",
              }),
              VerifyBtnWithState({
                d3eState: this.d3eState,
                _onVerifySuccessHandler: this.onVerifySuccessHandler,
                key: "1",
              }),
            ],
            className: "x38 hc",
            key: "4",
          }),
        ],
        className: ui.join(
          this.props.className,
          "BuilderEmailVerificationView x4ba hc h"
        ),
        ...copyBaseUIProps(this.props),
      }),
      scrollDirection: ui.Axis.vertical,
      className: "BuilderEmailVerificationView x4ba hc h",
      controller: this.d3eState.id1ScrollController,
    });
  }
  public onInit = (): void => {
    this.setToken(this.generatedToken);
  };
  public onVerifySuccessHandler = async (
    d3eState: BuilderEmailVerificationViewRefs
  ): Promise<void> => {
    this.setShowOtpValidationError(false);

    let result: LoginResult = await Query.get().loginWithOTP(
      UsageConstants.QUERY_LOGINWITHOTP_BUILDEREMAILVERIFICATIONVIEW_EVENTHANDLERS_ONVERIFYSUCCESSHANDLER_BLOCK,
      { token: this.token, code: this.userEnteredOtp }
    );

    if (result.success) {
      this.setUser(result.userObject as Developer);

      this.user.setEmail(this.email);

      this.onVerificationSuccess(this.user);
    } else {
      this.setShowOtpValidationError(true);
    }
  };
  public otpHandler = async (): Promise<void> => {
    let otp: OneTimePassword = new OneTimePassword({
      input: this.email.toLowerCase(),
      inputType: "email",
      userType: "Developer",
    });

    let result: Result<OneTimePassword> = await otp.save();

    if (result.status === ResultStatus.Success) {
      this.setToken(otp.token);
    }
  };
  public onResendHandler = (
    d3eState: BuilderEmailVerificationViewRefs
  ): void => {
    this.otpHandler();
  };
  public setOtpHandler = (
    value: string,
    d3eState: BuilderEmailVerificationViewRefs
  ): void => {
    this.setUserEnteredOtp(value);
  };
  public setOtpError = (d3eState: BuilderEmailVerificationViewRefs): void => {
    this.setShowOtpValidationError(false);
  };
  public onTapHandler = (d3eState: BuilderEmailVerificationViewRefs): void => {
    this.onLoginClick();
  };
  public get onLoginClick(): _BuilderEmailVerificationViewOnLoginClick {
    return this.props.onLoginClick;
  }
  public get onVerificationSuccess(): _BuilderEmailVerificationViewOnVerificationSuccess {
    return this.props.onVerificationSuccess;
  }
  public otpboxonChanged = (
    val: string,
    d3eState: BuilderEmailVerificationViewRefs
  ): void => {
    this.setUserEnteredOtp(val);
  };
  public get resendBtn() {
    return this.d3eState.resendBtn;
  }
  public get verifyBtn() {
    return this.d3eState.verifyBtn;
  }
}
export default function BuilderEmailVerificationView(
  props: BuilderEmailVerificationViewProps
) {
  return React.createElement(_BuilderEmailVerificationViewState, {
    ..._BuilderEmailVerificationViewState.defaultProps,
    ...props,
  });
}
